import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveBar } from '@nivo/bar';

import theme from '../theme';
import { getCurrentUser } from '../api/userSettings';
import { getInspectionStats } from '../api/dashboard';
import { MONTH_FULL_LIST } from '../constants';
import ModalDefaultDraggable from '../molecules/ModalDefaultDraggable';
import CompletedInspectionsPage from '../pages/CompletedInspectionsPage';

type Stat = {
  month: number;
  year: number;
  inspectionCount: number;
};

type ChartRow = Stat & { label: string };

const formatMonthLabel = (item: { month: number; year: number }) => {
  const monthName = MONTH_FULL_LIST[item.month - 1]?.slice(0, 3) || '';
  const yy = String(item.year).slice(-2);
  return `${monthName} '${yy}`;
};

const CompletedInspectionsGraph = () => {
  const [currentUser, setCurrentUser] = React.useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = React.useState<number | null>(null);
  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [chartData, setChartData] = React.useState<ChartRow[]>([]);
  const [showModal, setShowModal] = React.useState(false);

  React.useEffect(() => {
    getCurrentUser().then(res => setCurrentUser(res.data.id));
  }, []);

  React.useEffect(() => {
    if (currentUser) {
      getInspectionStats(currentUser).then(({ data }) => {
        const sorted = [...data].sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.month - b.month;
        });
        const rows: ChartRow[] = sorted.map(d => ({ ...d, label: formatMonthLabel(d) }));
        setChartData(rows);
      });
    }
  }, [currentUser]);

  const closeModal = () => setShowModal(false);

  const totalCompleted = React.useMemo(
    () => chartData.reduce((sum, d) => sum + (d.inspectionCount || 0), 0),
    [chartData],
  );

  const maxValue = React.useMemo(
    () => chartData.reduce((m, d) => Math.max(m, d.inspectionCount || 0), 0),
    [chartData],
  );

  const chartHeight = Math.max(130, chartData.length * 32 + 44);

  return (
    <Container>
      <Summary>
        <SummaryNumber>{totalCompleted}</SummaryNumber>
        <SummaryText>
          total inspection{totalCompleted === 1 ? '' : 's'} completed
          {chartData.length > 0 && (
            <SummaryHint>
              {' '}across {chartData.length} {chartData.length === 1 ? 'month' : 'months'}
            </SummaryHint>
          )}
        </SummaryText>
      </Summary>

      {chartData.length === 0 && <EmptyState>No completed inspections yet.</EmptyState>}

      {chartData.length > 0 && (
        <BarWrapper style={{ height: chartHeight }}>
          <ResponsiveBar
            data={chartData}
            keys={['inspectionCount']}
            indexBy="label"
            layout="horizontal"
            colors={[theme.palette.primary600]}
            padding={0.45}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            borderRadius={4}
            margin={{ top: 6, right: 28, bottom: 28, left: 70 }}
            enableGridX
            enableGridY={false}
            gridXValues={Math.min(5, Math.max(1, maxValue))}
            axisBottom={{
              tickSize: 0,
              tickPadding: 10,
              tickValues: Math.min(5, Math.max(1, maxValue)),
              format: (v: any) => (Number.isInteger(v) ? v : ''),
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 10,
            }}
            axisRight={null}
            axisTop={null}
            label={d => `${d.value}`}
            labelSkipWidth={18}
            labelTextColor="#ffffff"
            isInteractive
            tooltip={({ value, indexValue }) => (
              <TooltipBox>
                <TooltipMonth>{indexValue}</TooltipMonth>
                <TooltipValue>
                  {value} inspection{value === 1 ? '' : 's'}
                </TooltipValue>
              </TooltipBox>
            )}
            theme={{
              fontFamily: `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`,
              axis: {
                ticks: {
                  text: {
                    fontSize: 12,
                    fontWeight: 500,
                    fill: theme.palette.neutral500,
                  },
                },
              },
              grid: {
                line: {
                  stroke: theme.palette.neutral200,
                  strokeDasharray: '3 3',
                },
              },
              labels: {
                text: {
                  fontSize: 12,
                  fontWeight: 700,
                },
              },
            }}
            onMouseEnter={(_d, e) => {
              (e.currentTarget as SVGElement).style.cursor = 'pointer';
            }}
            onClick={data => {
              setSelectedMonth(data.data.month);
              setSelectedYear(data.data.year);
              setShowModal(true);
            }}
          />
        </BarWrapper>
      )}

      {showModal && (
        <ModalDefaultDraggable title="Completed Inspections" onClose={closeModal} extraWide>
          <CompletedInspectionsPage month={selectedMonth} year={selectedYear} />
        </ModalDefaultDraggable>
      )}
    </Container>
  );
};

export default CompletedInspectionsGraph;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-sizing: border-box;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const Summary = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 0 4px;
  min-width: 0;
`;

const SummaryNumber = styled.span`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1;
  letter-spacing: -0.01em;
`;

const SummaryText = styled.span`
  font-size: 0.875rem;
  color: var(--neutral-600);
  line-height: 1.4;
`;

const SummaryHint = styled.span`
  color: var(--neutral-500);
`;

const BarWrapper = styled.div`
  width: 100%;
  min-height: 130px;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--neutral-500);
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
`;

const TooltipBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
  font-family: ${FONT_STACK};
`;

const TooltipMonth = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const TooltipValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
`;
